import { useState } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";

import heroImage from "@/assets/onboarding-welcome-hero.webp";
import slideOneImage from "@/assets/onboarding-slide-2-v4.webp";
import slideTwoImage from "@/assets/onboarding-slide-3-v2.webp";
import slideThreeImage from "@/assets/onboarding-slide-4.webp";

import { ModeSelection } from "./ModeSelection";
import { OnboardingSlideZero } from "./onboarding/OnboardingSlideZero";
import { OnboardingSlideOne } from "./onboarding/OnboardingSlideOne";
import { OnboardingSlideTwo } from "./onboarding/OnboardingSlideTwo";
import { OnboardingSlideThree } from "./onboarding/OnboardingSlideThree";

const ONBOARDING_KEY = "onboarding_completed";

export function useOnboarding() {
  const isCompleted = localStorage.getItem(ONBOARDING_KEY) === "true";
  
  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
  };

  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_KEY);
  };

  return { isCompleted, completeOnboarding, resetOnboarding };
}

interface OnboardingProps {
  onComplete: () => void;
}

type OnboardingStep = 'slides' | 'mode-selection';

export function Onboarding({ onComplete }: OnboardingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [step, setStep] = useState<OnboardingStep>('slides');
  const { completeOnboarding } = useOnboarding();

  // Preload onboarding visuals to avoid "text first, then image" popping.
  useEffect(() => {
    const sources = [heroImage, slideOneImage, slideTwoImage, slideThreeImage];
    const imgs = sources.map((src) => {
      const img = new Image();
      img.src = src;
      return img;
    });
    return () => {
      // keep reference for cleanup (no-op, helps GC in some browsers)
      imgs.length = 0;
    };
  }, []);

  // Slide types: 'custom' for custom components
  const slides = [
    {
      type: 'custom' as const,
      component: OnboardingSlideZero,
    },
    {
      type: 'custom' as const,
      component: OnboardingSlideOne,
    },
    {
      type: 'custom' as const,
      component: OnboardingSlideTwo,
    },
    {
      type: 'custom' as const,
      component: OnboardingSlideThree,
    },
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      // Move to mode selection instead of auth
      setStep('mode-selection');
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleModeSelectionComplete = () => {
    completeOnboarding();
    onComplete();
    navigate("/");
  };

  const handleChooseCloud = () => {
    completeOnboarding();
    onComplete();
    navigate("/auth");
  };

  const handleSkip = () => {
    setStep('mode-selection');
  };

  const isLastSlide = currentSlide === slides.length - 1;

  // Show mode selection screen
  if (step === 'mode-selection') {
    return (
      <ModeSelection 
        onComplete={handleModeSelectionComplete} 
        onChooseCloud={handleChooseCloud}
      />
    );
  }

  const currentSlideData = slides[currentSlide];
  const SlideComponent = currentSlideData.component;
  return (
    <div className="fixed inset-0 bg-background overflow-hidden">
      {/* Custom slide content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="h-full"
        >
          <SlideComponent />
        </motion.div>
      </AnimatePresence>
      
      {/* Skip button */}
      <div className="absolute top-0 left-0 right-0 flex justify-end p-4 z-50" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          className="text-muted-foreground hover:text-foreground hover:bg-muted min-h-[44px] min-w-[44px]"
        >
          {t("onboarding.skip")}
        </Button>
      </div>

      {/* Bottom navigation with liquid glass */}
      <div className="absolute bottom-0 left-0 right-0 px-6 pb-8 safe-area-bottom z-10 pt-8">
        {/* Glass panel background */}
        <div 
          className="absolute inset-x-0 bottom-0 top-0 -z-10"
          style={{
            background: 'linear-gradient(to top, hsl(var(--background) / 0.95), hsl(var(--background) / 0.6), transparent)',
            backdropFilter: 'blur(20px) saturate(150%)',
          }}
        />
        
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? "bg-primary w-8 shadow-lg"
                  : "bg-muted-foreground/30 w-2.5 hover:bg-muted-foreground/50"
              }`}
              style={index === currentSlide ? {
                boxShadow: '0 0 12px hsl(var(--primary) / 0.5)',
              } : undefined}
            />
          ))}
        </div>

        {/* Navigation buttons with glass effect */}
        <div className="flex gap-3">
          {currentSlide > 0 && (
            <button
              onClick={handlePrev}
              className="flex-shrink-0 w-14 h-12 rounded-2xl flex items-center justify-center transition-all active:scale-95"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--card) / 0.6), hsl(var(--card) / 0.3))',
                backdropFilter: 'blur(20px) saturate(180%)',
                border: '1px solid hsl(var(--border) / 0.3)',
                boxShadow: '0 4px 20px hsl(var(--primary) / 0.1)',
              }}
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex-1 h-12 rounded-2xl flex items-center justify-center gap-2 font-semibold transition-all active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(270 55% 50%))',
              boxShadow: '0 8px 32px hsl(var(--primary) / 0.4), inset 0 1px 0 hsl(0 0% 100% / 0.2)',
              border: '1px solid hsl(0 0% 100% / 0.1)',
            }}
          >
            <span className="text-primary-foreground">{t("onboarding.next")}</span>
            <ArrowRight className="w-5 h-5 text-primary-foreground" />
          </button>
        </div>
      </div>
    </div>
  );

}
